import Vue from 'vue';
import axios from 'axios';

/**
 * Фатальные ошибки приложения — те, после которых показывать текущий экран бессмысленно.
 *
 * Раньше каждая такая ошибка уводила на отдельный маршрут /error/500. Это ломало главное:
 * перезагрузка страницы повторяла саму заглушку, а не то, что пользователь пытался открыть,
 * и выйти из неё можно было только руками правя адрес. Теперь URL не меняется, а поверх
 * экрана поднимается оверлей — F5 повторяет исходный запрос и обычно чинит ситуацию.
 *
 * Состояние живёт здесь, а не в Vuex: модуль импортируют http.js и перехватчики, а стор сам
 * тянет http.js через actions — через Vuex получился бы цикл импортов.
 */
export const APP_ERROR_KIND = {
    // Ответа нет вовсе: обрыв сети, ERR_FAILED, отказ CORS на странице 502 от прокси.
    NETWORK: 'network',
    // 5xx.
    SERVER: 'server',
    // Не загрузились справочники, интерфейс так и не открылся.
    BOOT: 'boot',
    FORBIDDEN: 'forbidden',
    AUTH: 'auth',
};

const PRESENTATIONS = {
    [APP_ERROR_KIND.NETWORK]: {
        code: '',
        title: 'Сервер не отвечает',
        description: 'Проверьте подключение к сети и попробуйте ещё раз.',
        action: 'reload',
        actionLabel: 'Перезагрузить страницу',
    },
    [APP_ERROR_KIND.SERVER]: {
        code: '500',
        title: 'На стороне сервера ошибка',
        description: 'Запрос не выполнен. Перезагрузите страницу или повторите позже.',
        action: 'reload',
        actionLabel: 'Перезагрузить страницу',
    },
    [APP_ERROR_KIND.BOOT]: {
        code: '',
        title: 'Не удалось загрузить приложение',
        description: 'Справочники не загрузились, поэтому интерфейс не открылся. Перезагрузите страницу.',
        action: 'reload',
        actionLabel: 'Перезагрузить страницу',
    },
    [APP_ERROR_KIND.FORBIDDEN]: {
        code: '403',
        title: 'Нет доступа',
        description: 'У вашей учётной записи нет прав на этот раздел. Обратитесь к администратору системы.',
        action: 'home',
        actionLabel: 'На главную',
    },
    [APP_ERROR_KIND.AUTH]: {
        code: '401',
        title: 'Не удалось войти в систему',
        description: 'Служба авторизации не ответила. Попробуйте войти заново.',
        action: 'home',
        actionLabel: 'Войти заново',
    },
};

// closable — есть ли под оверлеем рабочий экран, на который можно вернуться. Ошибку в уже
// работающем приложении закрыть можно (упал один фоновый запрос — незачем запирать страницу),
// а несостоявшуюся загрузку нельзя: под ней только спиннер, и закрытие оставило бы тупик.
export const appErrorState = Vue.observable({ kind: null, closable: true });

/**
 * Какой оверлей заслуживает эта ошибка — и заслуживает ли вообще.
 *
 * @param {object} error Ошибка axios.
 * @returns {string|null} Вид из APP_ERROR_KIND либо null, если показывать оверлей не нужно.
 */
export function resolveAppErrorKind(error) {
    if (!error) {
        return null;
    }

    // Отмена — не сбой: у неё тоже нет response, и без этой проверки каждая смена фильтра в
    // списке обращений (там запрос отменяется предыдущим) поднимала бы оверлей «нет связи».
    if (error.code === 'ERR_CANCELED' || axios.isCancel(error)) {
        return null;
    }

    // Ответа нет — статус читать не из чего. Сюда же попадает 502 от прокси: браузер отдаёт
    // такой ответ как сетевой сбой, потому что на странице ошибки прокси нет заголовков CORS.
    if (!error.response) {
        return APP_ERROR_KIND.NETWORK;
    }

    const status = error.response.status;

    if (status >= 500) {
        return APP_ERROR_KIND.SERVER;
    }
    if (status === 403) {
        return APP_ERROR_KIND.FORBIDDEN;
    }
    if (status === 401) {
        return APP_ERROR_KIND.AUTH;
    }

    // Прикладные 4xx (404, 422 и прочие) разбирает вызывающий код: у него есть контекст, а
    // перезагрузка страницы такую ошибку всё равно не чинит.
    return null;
}

/**
 * Описание для экрана. Неизвестный вид отдаёт описание сбоя сервера — так на экран
 * гарантированно не попадёт пустота.
 *
 * @param {string} kind
 * @returns {{code: string, title: string, description: string, action: string, actionLabel: string}}
 */
export function describeAppError(kind) {
    return PRESENTATIONS[kind] || PRESENTATIONS[APP_ERROR_KIND.SERVER];
}

/**
 * Поднимает оверлей. Повторный вызов ничего не меняет: один упавший запрос обычно тянет за
 * собой соседние, и пользователь должен видеть то, что случилось первым, а не последний шум.
 *
 * @param {string} kind
 * @param {{closable?: boolean}} [options] closable: false — если под оверлеем нет рабочего
 *        экрана (приложение так и не загрузилось), закрывать его некуда.
 */
export function showAppError(kind, options = {}) {
    if (appErrorState.kind) {
        return;
    }

    appErrorState.kind = kind;
    appErrorState.closable = options.closable !== false;
}

export function clearAppError() {
    appErrorState.kind = null;
    appErrorState.closable = true;
}
